/**
 * 下のナビの本（タブ）。管理UIの「下のナビ」（/nav-tabs）で名前・つながる画面・最初に見せるタブ・絵・革の色・並び・出す/隠すを決めて公開する。
 * 形と画面の一覧は管理UIの local-ui/src/types/nav-tabs.ts と同じ。正本は Firestore の appSettings/navTabs（公開版）。
 * 開く・閉じる動きと、本の見た目そのものはコード（globals.css の library-nav-item）で決める
 */
import type { LibraryIconName } from "@/components/ui/LibraryIcon";
import { GACHA_ENTRY_PATH } from "./gacha-entry";

/**
 * つながる画面。own＝その画面にいるとき光らせる道（長く一致する方が勝つ）、except＝own に含めない道、
 * related＝その画面を持つ本が無いときに代わりに光らせる道。views＝最初に見せるタブ（URL の指定を画面が読む）
 */
export const NAV_DESTINATIONS = {
  home: { label: "図書館（ホーム）", path: "/", own: ["/"], related: ["/backgrounds", "/party", "/quests", "/goals", "/presents"] },
  map: { label: "地図", path: "/map", own: ["/map"] },
  gachaSelect: { label: "本を引く（選ぶ画面）", path: "/gacha/select", own: ["/gacha/select"], related: ["/gacha", "/spirit/gacha"] },
  novelGacha: { label: "小説ガチャ", path: "/gacha", own: ["/gacha"] },
  spiritGacha: { label: "精霊ガチャ", path: "/spirit/gacha", own: ["/spirit/gacha"] },
  shelf: {
    label: "書庫",
    path: "/shelf",
    own: ["/shelf"],
    views: { list: { label: "書物一覧", query: "" }, shelf: { label: "本棚", query: "mainTab=shelf" }, vocab: { label: "単語帳", query: "mainTab=vocab" } },
  },
  spirit: { label: "精霊（獲得した精霊）", path: "/spirit", own: ["/spirit"], except: ["/spirit/gacha"], related: ["/encyclopedia", "/party"] },
  party: { label: "手持ちの精霊", path: "/party", own: ["/party"] },
  quests: { label: "今日の読み方", path: "/quests", own: ["/quests"] },
  goals: {
    label: "ログインボーナスと報酬一覧",
    path: "/goals",
    own: ["/goals"],
    views: { goals: { label: "報酬一覧", query: "" }, login: { label: "ログインボーナス", query: "tab=login" } },
  },
  presents: { label: "プレゼント", path: "/presents", own: ["/presents"] },
  encyclopedia: {
    label: "図鑑",
    path: "/encyclopedia",
    own: ["/encyclopedia"],
    views: { spirits: { label: "精霊図鑑", query: "" }, materials: { label: "素材図鑑", query: "tab=materials" } },
  },
  backgrounds: { label: "背景（場所を選ぶ）", path: "/backgrounds", own: ["/backgrounds"] },
  settings: { label: "設定", path: "/settings", own: ["/settings"] },
} as const satisfies Record<string, {
  label: string;
  path: string;
  own: readonly string[];
  except?: readonly string[];
  related?: readonly string[];
  views?: Record<string, { label: string; query: string }>;
}>;
export type NavDestinationId = keyof typeof NAV_DESTINATIONS;

/** 革の色（globals.css の .library-nav-item[data-leather]） */
export const NAV_LEATHERS = ["wine", "green", "navy", "brown", "violet", "iron"] as const;
export type NavLeather = (typeof NAV_LEATHERS)[number];

export const NAV_ICONS = ["library", "map", "book", "shelf", "spirit", "settings", "gift", "quest", "target", "arrow"] as const satisfies readonly LibraryIconName[];

export interface NavTab {
  /** 本の名前（管理UIで作るときに決まり、変わらない） */
  id: string;
  label: string;
  destination: NavDestinationId;
  /** 最初に見せるタブ（つながる画面に views があるときだけ） */
  view?: string;
  icon: LibraryIconName;
  /** 管理UIの「UIアイコン」で上げた絵の枠（nav_library など）。絵があれば線のアイコンの代わりに出す */
  imageKey?: string;
  leather: NavLeather;
  hidden?: boolean;
}

/** 本は6冊まで（320px の幅に収まる数） */
export const NAV_MAX_SHOWN = 6;

/** 公開前・読めないときの並び（いまのナビ） */
export const DEFAULT_NAV_TABS: NavTab[] = [
  { id: "nav_library", label: "図書館", destination: "home", icon: "library", imageKey: "nav_library", leather: "wine" },
  { id: "nav_map", label: "地図", destination: "map", icon: "map", imageKey: "nav_map", leather: "green" },
  { id: "nav_gacha", label: "本を引く", destination: "gachaSelect", icon: "book", imageKey: "nav_gacha", leather: "navy" },
  { id: "nav_reading", label: "書庫", destination: "shelf", icon: "shelf", imageKey: "nav_reading", leather: "brown" },
  { id: "nav_spirit", label: "精霊", destination: "spirit", icon: "spirit", imageKey: "nav_spirit", leather: "violet" },
  { id: "nav_settings", label: "設定", destination: "settings", icon: "settings", imageKey: "nav_settings", leather: "iron" },
];

/** つながる画面の URL（最初に見せるタブの指定つき）。ホームの部品の行き先にも使う */
export function destinationHref(destination: NavDestinationId, view?: string): string {
  // ガチャを選ぶ画面を眠らせているあいだは、本を引く入口（小説ガチャ）へ（src/lib/gacha-entry.ts）
  if (destination === "gachaSelect") return GACHA_ENTRY_PATH;
  const dest = NAV_DESTINATIONS[destination];
  const views = "views" in dest ? (dest.views as Record<string, { query: string }>) : undefined;
  const query = view && views?.[view]?.query;
  return query ? `${dest.path}?${query}` : dest.path;
}

/** 本を押したときに開く URL */
export function navTabHref(tab: NavTab): string {
  return destinationHref(tab.destination, tab.view);
}

const matches = (pathname: string, prefix: string) =>
  prefix === "/" ? pathname === "/" : pathname === prefix || pathname.startsWith(`${prefix}/`);

/** いまの画面を持つ本（own が一番長く一致する本）。無ければ null */
export function ownNavTab(tabs: NavTab[], pathname: string): string | null {
  let best: { id: string; len: number } | null = null;
  for (const tab of tabs) {
    const dest = NAV_DESTINATIONS[tab.destination];
    if ("except" in dest && dest.except.some((p) => matches(pathname, p))) continue;
    for (const p of dest.own) {
      if (matches(pathname, p) && (!best || p.length > best.len)) best = { id: tab.id, len: p.length };
    }
  }
  return best?.id ?? null;
}

/** 自分の本を持たない画面を直接開いたときに光らせる本（related が一致する本・それも無ければホームの本） */
export function fallbackNavTab(tabs: NavTab[], pathname: string): string | null {
  const related = tabs.find((tab) => {
    const dest = NAV_DESTINATIONS[tab.destination];
    return "related" in dest && dest.related.some((p) => matches(pathname, p));
  });
  return related?.id ?? tabs.find((tab) => tab.destination === "home")?.id ?? null;
}

/** Firestore の値を読む。決まりに合わなければ null（ナビはいまの並びのまま）。管理UIの normalize と同じ決まり */
export function parseNavTabs(data: unknown): NavTab[] | null {
  const raw = (data as { tabs?: unknown } | undefined)?.tabs;
  if (!Array.isArray(raw)) return null;
  const tabs: NavTab[] = [];
  for (const t of raw as Partial<NavTab>[]) {
    if (!t || typeof t.id !== "string" || typeof t.label !== "string" || !t.label.trim()) return null;
    if (!t.destination || !(t.destination in NAV_DESTINATIONS)) return null;
    if (!t.icon || !(NAV_ICONS as readonly string[]).includes(t.icon)) return null;
    if (!t.leather || !(NAV_LEATHERS as readonly string[]).includes(t.leather)) return null;
    tabs.push({
      id: t.id,
      label: t.label.trim(),
      destination: t.destination,
      view: typeof t.view === "string" ? t.view : undefined,
      icon: t.icon,
      imageKey: typeof t.imageKey === "string" && t.imageKey ? t.imageKey : undefined,
      leather: t.leather,
      hidden: t.hidden === true,
    });
  }
  const shown = tabs.filter((t) => !t.hidden);
  // ホームへ戻る本は必ず出す・出す本は1〜6冊
  if (!shown.some((t) => t.destination === "home") || shown.length > NAV_MAX_SHOWN) return null;
  return shown;
}
